import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState, useEffect } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || 'free-platforms');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles
    .filter((a) => a.categoryId === currentCategory?.id)
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 看片网站推荐2025` : '分类浏览',
    description: currentCategory?.description || '',
    url: typeof window !== 'undefined' ? window.location.href : '',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />
      <main>
        <section className="w-full px-4 md:px-6 pt-24 pb-8 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '分类浏览' },
              ]}
            />
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
                {currentCategory?.name || '分类浏览'}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-[700px] leading-relaxed">
                {currentCategory?.description || ''}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap pb-8 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
              {categoryArticles.map((article) => (
                <article
                  key={article.id}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                >
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-3 right-3">
                        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          {article.rating}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.publishedAt}
                        </span>
                      </div>
                      <h2 className="text-base font-bold text-foreground-900 mb-2 font-heading group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-inbox-line text-2xl"></i>
                </span>
                <p className="text-foreground-600">该分类暂无测评文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}